import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep, forEach, get, isObject, set } from "lodash";
import isEmpty from "lodash/isEmpty";
import {
  filterData,
  getColumnsData,
} from "../../components/ui/list/filterHelpers";

const INIT_DATA = {
  activeFilters: {},
  filtersData: {},
  filterCache: {},
  currentTableId: null,

  preFilteredPatients: [],
  currentColumnsObject: [],
};

export const patientListDataSlice = createSlice({
  name: "patientListData",
  initialState: INIT_DATA,
  reducers: {
    setFiltersData: (state, filters) => {
      state.filtersData = filters.payload;
    },
    setPatientListFilter: (state, filters) => {
      state.activeFilters = filters.payload;
    },
    updatePatientListFilter: (state, filters) => {
      state.activeFilters = {
        ...state.activeFilters,
        ...filters.payload,
      };
    },

    // Cache
    setFilterCache: (state, filters) => {
      state.filterCache = {
        ...state.filterCache,
        ...filters.payload,
      };
    },
    setCurrentTableId: (state, tableId) => {
      state.currentTableId = tableId.payload;
    },

    // For cascaded filters data
    setPreFilteredPatients: (state, patients) => {
      state.preFilteredPatients = patients.payload;
    },
    setCurrentColumnsObject: (state, columns) => {
      state.currentColumnsObject = columns.payload;
    },
  },
});

// Setters
export const {
  setPatientListFilter,
  updatePatientListFilter,
  setFilterCache,
  setFiltersData,
  setCurrentTableId,
  setPreFilteredPatients,
  setCurrentColumnsObject,
} = patientListDataSlice.actions;

// Variable
export const patientListFilters = (state) =>
  state.patientListData.activeFilters;
export const patientListFiltersData = (state) =>
  state.patientListData.filtersData;
export const patientListFilterCache = (state) =>
  state.patientListData.filterCache;
export const patientListCurrentTable = (state) =>
  state.patientListData.currentTableId;

// Functions
export const applyCurrentColumnFilter =
  (col, filter) => (dispatch, getState) => {
    if (!filter) return;
    const columnsCurrentFilters = getState().patientListData.activeFilters;
    const hasFilter = !isEmpty(filter);

    if (hasFilter) {
      dispatch(
        setPatientListFilter({
          ...columnsCurrentFilters,
          [col.id]: { column: col, filter },
        })
      );
    } else {
      let clonedFilters = { ...(columnsCurrentFilters ?? {}) };
      delete clonedFilters[col.id];
      dispatch(setPatientListFilter(clonedFilters));
    }
  };

export const getCascadedFiltersData = (columnId) => (dispatch, getState) => {
  const filters = getState().patientListData.activeFilters;
  let cascadedData = [];

  // If there are other active filters, apply them to get cascaded data
  if (!isEmpty(filters)) {
    let filteredPatients = cloneDeep(
      getState().patientListData.preFilteredPatients
    );
    const filtersData = getState().patientListData.filtersData;
    cascadedData = get(filtersData, columnId, []);
    const columns = getState().patientListData.currentColumnsObject;
    const currentColumn = columns.filter((col) => col.id === columnId);

    // If there are other active filters, apply them to get cascaded data
    forEach(filters, (filter, colId) => {
      if (colId === columnId) return false; // Skip self

      // Apply filter to cascaded data
      filteredPatients = filterData(filteredPatients, { [colId]: filter });
      cascadedData = getColumnsData(filteredPatients, currentColumn);
    });
  }

  // If cascadedData is an object, get the array for the specific columnId
  if (isObject(cascadedData)) {
    cascadedData = cascadedData[columnId] || [];
  }

  return cascadedData;
};

export default patientListDataSlice.reducer;
